import * as QcoActions from './qco.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { Injectable } from '@angular/core';
import { LINE_SETUP_TICKET_DEFAULT_PAGINATION_OPTION, TQcoActionTypes } from './qco.models';
import { catchError, finalize, from, map, of, switchMap, take } from 'rxjs';
import { FactoryFacade } from '../factory/factory.facade';

@Injectable({ providedIn: 'root' })
export class QcoEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private factoryFacade: FactoryFacade,
  ) {}

  getLineSetupTicketsListing = createEffect(() =>
    this.actions$.pipe(
      ofType(TQcoActionTypes.GetLineSetupTicketsListing),
      switchMap((action: QcoActions.GetLineSetupTicketsListing) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getLineSetupTicketListing(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          switchMap((res) => [
            new QcoActions.GetLineSetupTicketsListingSuccess(res),
            ...(action.resetPaginationOption
              ? [new QcoActions.SetLineSetupTicketPaginationOption(LINE_SETUP_TICKET_DEFAULT_PAGINATION_OPTION)]
              : []),
          ]),
          catchError(() => {
            return of(new QcoActions.GetLineSetupTicketsListingFailure());
          }),
        ),
      ),
    ),
  );

  getLineSetupTicketsSubtasks = createEffect(() =>
    this.actions$.pipe(
      ofType(TQcoActionTypes.GetLineSetupTicketsSubtasks),
      switchMap((action: QcoActions.GetLineSetupTicketsSubtasks) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getLineSetupTicketSubTaskList(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new QcoActions.GetLineSetupTicketsSubtasksSuccess(res)),
          catchError(() => {
            return of(new QcoActions.GetLineSetupTicketsSubtasksFailure());
          }),
        ),
      ),
    ),
  );

  fetchLineSetupTicketNewSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TQcoActionTypes.FetchLineSetupTicketNewSetup),
      switchMap((action: QcoActions.FetchLineSetupTicketNewSetup) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getLineSetupTicketDetails(action.lineSetupTicketId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new QcoActions.FetchLineSetupTicketNewSetupSuccess(res)),
          catchError(() => {
            return of(new QcoActions.FetchLineSetupTicketNewSetupFailure());
          }),
        ),
      ),
    ),
  );

  fetchLineSetupTicketCurrentSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TQcoActionTypes.FetchLineSetupTicketCurrentSetup),
      switchMap((action: QcoActions.FetchLineSetupTicketCurrentSetup) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.getAllWorkstationsForProductionArea(factoryId, action.locationId).pipe(
          finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)),
          map((res) => new QcoActions.FetchLineSetupTicketCurrentSetupSuccess(res)),
          catchError(() => of(new QcoActions.FetchLineSetupTicketCurrentSetupFailure())),
        ),
      ),
    ),
  );
}
